import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { UPDATE_USER_DATA } from "../redux/features/user/userSlice";
import { useLocation } from "react-router-dom";
import NavMobileMenu from "./NavMobileMenu";
import { handleApi } from "../utils/authentication";
import { toast } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import { Notifications } from "./Notifications";
import { customStyles } from "../utils/select_style";
import { getIconSrc } from "../helpers/getImgSrc";
import { getCurrentPageName } from "../helpers/getCurrentPageName";
import DropdownFilter from "../partials/filter/DropdownFilter";
import { UPDATE_FILTER_DATA } from "../redux/features/user/filterSlice";
import DateRangePicker from "../partials/date-range-picker/DateRangePicker";
import { getSvgIcons } from "../helpers/getReactIcons";
import { CLEAR_USER_TOUR } from "../redux/features/user/userTourSlice";
import { useNavigate } from "react-router-dom";
import {
  UPDATE_IS_SYNCING,
  SET_JOB_COUNT,
  SET_REMAINING_JOB_COUNT,
  RESET_SYNC_PROGRESS,
} from "../redux/features/user/userSlice";
import {
  clientHasAccess,
  applySubscriptionAccess,
  getSubscriptionLockedFallbackPath,
} from "../utils/subscriptionAccess";

const UserNavbar = ({
  setIsOpenSidebar,
  clientList,
  programList,
  handleClientSelectChange,
  handleProgramSelectChange,
  customStyles,
  user,
  location,
}) => {
  const dispatch = useDispatch();
  const [internalCampaignList, setInternalCampaignList] = useState([]);
  const filter = useSelector((state) => state?.filter);
  const is_syncing = useSelector((state) => state?.user?.is_syncing);
  const job_count = useSelector((state) => state?.user?.job_count);
  const remaining_job_count = useSelector(
    (state) => state?.user?.remaining_job_count,
  );
  const pollingIntervalRef = useRef(null);
  const jobCountSetRef = useRef(false);
  const [syncError, setSyncError] = useState(null);
  const [showPercent, setShowPercent] = useState(false);

  const currentJobCountRef = useRef(job_count);

  const [customRange, setCustomRange] = useState({
    startDate: null,
    endDate: null,
  });

  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const navigate = useNavigate();

  const getHeaderFilters = () => {
    if (location.pathname.includes("client/campaigns")) {
      return [
        "programs",
        "status",
        "date_range",
        "campaign",
        "ads_platfrom_filter",
      ];
    }
    if (location.pathname.includes("/client/settings/integrations")) {
      return ["platform"];
    }
    if (location.pathname.includes("/client/dashboards/home")) {
      return ["date_range"];
    }
    if (location.pathname.includes("/client/dashboards/paid_social")) {
      return ["date_range"];
    }
    if (location.pathname.includes("/client/dashboards/organic_social")) {
      return ["date_range"];
    }
    if (location.pathname.includes("/client/dashboards/paid_media")) {
      return ["date_range"];
    }
    return [];
  };

  // Polling logic for sync status
  useEffect(() => {
    // Debug log

    if (is_syncing && user?.client_id && user?.token) {
      // Reset jobCountSetRef for a new sync session
      jobCountSetRef.current = false;
      // Start polling every 30s if not already started
      if (!pollingIntervalRef.current) {
        const interval = setInterval(async () => {
          try {
            const data = await handleApi(
              `check_sync_status?client_id=${user.client_id}`,
              "GET",
              {},
              null,
            );
            if (typeof data?.is_syncing !== "undefined") {
              dispatch(UPDATE_IS_SYNCING(!!data.is_syncing));

              if (typeof data?.job_count !== "undefined") {
                let newJobCount = Number(data.job_count);
                // let currentJobCount = job_count;
                let currentJobCount = currentJobCountRef.current;

                if (newJobCount > currentJobCount) {
                  dispatch(SET_JOB_COUNT(newJobCount));
                }
              }
              if (typeof data?.remaining_job_count !== "undefined") {
                dispatch(
                  SET_REMAINING_JOB_COUNT(Number(data.remaining_job_count)),
                );
              }
              if (!data.is_syncing) {
                clearInterval(interval);
                pollingIntervalRef.current = null;
                dispatch(RESET_SYNC_PROGRESS());
                currentJobCountRef.current = 0;
                setShowPercent(false);
                console.log("Sync complete, stopped polling");
                toast.success("Sync Completed", {
                  position: "bottom-right",
                  autoClose: 5000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true,
                  progress: undefined,
                  theme: "light",
                });
              }
            } else {
              setSyncError("");
              clearInterval(interval);
              pollingIntervalRef.current = null;
              dispatch(UPDATE_IS_SYNCING(false));
              dispatch(RESET_SYNC_PROGRESS());
              setShowPercent(false);
              currentJobCountRef.current = 0;
              //toastr position bottom right
              toast.success("Sync Completed", {
                position: "bottom-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
              });
              // toast.error('Failed to check sync status');
            }
          } catch (e) {
            setSyncError("Network error while checking sync status");
            clearInterval(interval);
            pollingIntervalRef.current = null;
            dispatch(UPDATE_IS_SYNCING(false));
            // dispatch(RESET_SYNC_PROGRESS());
            setShowPercent(false);
            console.log("Network error while checking sync status", e);
            toast.error("Network error while checking sync status");
          }
        }, 5000); // Changed from 30000 (30s) to 5000 (5s)
        pollingIntervalRef.current = interval;
      }
    } else {
      // If not syncing, clear polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        setShowPercent(false);
        // dispatch(RESET_SYNC_PROGRESS());
        console.log("Stopped polling interval (not syncing)");
      }
    }
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        //  setShowPercent(false);
        // dispatch(RESET_SYNC_PROGRESS());
        console.log("Cleanup: stopped polling interval");
      }
    };
    // eslint-disable-next-line
  }, [is_syncing, user?.client_id, user?.token]);

  const handleSyncData = async () => {
    if (is_syncing) return;

    // Start rotation immediately
    dispatch(UPDATE_IS_SYNCING(true));
    setShowPercent(false);

    //dispatch(RESET_SYNC_PROGRESS());
    try {
      let result = await handleApi(
        "sync_social_media_campaigns",
        "GET",
        {},
        null,
      );

      if (result?.success && typeof result?.job_count !== "undefined") {
        let currentJobCount = currentJobCountRef.current;
        if (Number(result.job_count) > currentJobCount) {
          dispatch(SET_JOB_COUNT(Number(result.job_count)));
        }

        dispatch(SET_REMAINING_JOB_COUNT(Number(result.job_count)));
        setShowPercent(true);
        // toast.success("Syncing Ad and Post Data");
        // Polling will start via useEffect
      } else {
        dispatch(UPDATE_IS_SYNCING(false));

        //  dispatch(RESET_SYNC_PROGRESS());
        setShowPercent(false);
        toast.error("Failed to Sync Data");
      }
    } catch (e) {
      dispatch(UPDATE_IS_SYNCING(false));

      // dispatch(RESET_SYNC_PROGRESS());
      setShowPercent(false);
      toast.error("Failed to Sync Data");
    }
  };

  const handleStatusSelectChange = (selected_option) => {
    dispatch(UPDATE_FILTER_DATA({ filter_campaign_status: selected_option }));
  };

  const handleCampaignSelectChange = (selected_option) => {
    dispatch(UPDATE_FILTER_DATA({ filter_campaign: selected_option }));
  };

  const handleDateRangeSelectChange = (selected_option) => {
    if (selected_option.value == "Custom Range") {
      setShowDateRangePicker(true);
    } else {
      dispatch(UPDATE_FILTER_DATA({ filter_date_range: selected_option }));
    }
  };

  const handlePlateformSelectChange = (selected_option) => {
    dispatch(UPDATE_FILTER_DATA({ filter_platform: selected_option }));
  };

  const handleAdsPlateformSelectChange = (selected_option) => {
    dispatch(
      UPDATE_FILTER_DATA({
        filter_ads_platform: selected_option
          ? selected_option
          : { value: "all", label: "All" },
      }),
    );
  };

  const resetClientTour = async () => {
    try {
      const formData = new FormData();

      formData.append("user_id", user?.user_id);
      formData.append("client_id", user?.client_id);
      formData.append("has_seen_tour", user?.has_seen_tour);
      let result = await handleApi(
        "update_application_tour_status",
        "POST",
        {},
        formData,
      );
      if (result?.success) {
        dispatch(
          UPDATE_USER_DATA({
            has_seen_tour: result?.data?.has_seen_tour,
          }),
        );
        dispatch(CLEAR_USER_TOUR());
        navigate("/client/dashboards/home");
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (customRange?.startDate || customRange?.endDate) {
      dispatch(
        UPDATE_FILTER_DATA({
          filter_date_range: {
            days: {
              value: "Custom Range",
              label: "Custom Range",
            },
            startDate: customRange?.startDate,
            endDate: customRange?.endDate,
          },
        }),
      );
    } else {
      dispatch(
        UPDATE_FILTER_DATA({
          filter_date_range: {
            value: null,
            label: "All Time",
          },
        }),
      );
    }
  }, [customRange?.startDate, customRange?.endDate]);

  const fetchInternalCampaignList = async () => {
    const data = new FormData();

    if (user?.program_id) {
      data.append("program_id", user?.program_id);
    } else {
      data.append("user_id", user?.user_id);
      data.append("client_id", user?.client_id);
      data.append("role_id", user?.role_id);
    }

    const result = await handleApi(
      `get_internal_campaign_list`,
      "POST",
      {},
      data,
    );
    if (result?.success) {
      setInternalCampaignList(result?.data?.internal_campaign_list || []);
    }
  };

  useEffect(() => {
    fetchInternalCampaignList();
    dispatch(
      UPDATE_FILTER_DATA({
        filter_campaign: {
          value: null,
          label: "All",
        },
      }),
    );
  }, [user?.program_id]);

  useEffect(() => {
    fetchInternalCampaignList();
  }, [filter?.filter_ads_platform]);

  useEffect(() => {
    if (job_count > currentJobCountRef.current) {
      currentJobCountRef.current = job_count;
    }
  }, [job_count]);

  const isReportsPage = location?.pathname?.includes("reprots_analytics");

  return (
    <div
      className="header d-flex align-items-center padding_lg"
      style={
        isReportsPage
          ? {
              backgroundColor: "#302843",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
            }
          : {}
      }
    >
      <div className="d-flex justify-content-between align-items-center w-100 flex-wrap">
        <i
          className="bi bi-justify-left d-block d-xl-none fs-2 p-2"
          style={isReportsPage ? { color: "#fff" } : {}}
          onClick={() => setIsOpenSidebar(true)}
        ></i>
        <NavMobileMenu
          handleClientSelectChange={handleClientSelectChange}
          handleProgramSelectChange={handleProgramSelectChange}
          customStyles={customStyles}
          clientList={clientList}
          programList={programList}
        />
        <div className="d-none d-xl-flex gap_lg align-items-center">
          <div className="nav_profile_image ">
            {user?.profile_image ? (
              <img
                src={user?.profile_image}
                className="img-fluid"
                alt="Profile"
              />
            ) : (
              <div
                style={{
                  width: "4vw",
                  height: "7.9vh",
                  borderRadius: "50%",
                  color: isReportsPage ? "#fff" : undefined,
                }}
                className="d-flex justify-content-center align-items-center"
              >
                <p className="mb-0">
                  {user?.first_name[0]?.toUpperCase()}
                  {user?.last_name[0]?.toUpperCase()}
                </p>
              </div>
            )}
          </div>
          <div
            className={`navbar_container d-none d-xl-flex   ${
              getHeaderFilters().length < 1
                ? "align-items-center"
                : "flex-column"
            }`}
          >
            <p
              className="mb-0 aeonik-medium"
              style={isReportsPage ? { color: "#fff" } : {}}
            >
              Hello, {user?.first_name}
            </p>
            {!user?.is_super_admin && (
              <>
                {getHeaderFilters().length > 0 ? (
                  <div className="d-none d-xl-flex  gap-2 align-items-center filters">
                    <p
                      className="mb-0 "
                      style={{
                        fontSize: "14px",
                        fontFamily: "aeonik_regular",
                      }}
                    >
                      Check out your source for
                    </p>
                    <div className="d-flex gap-2 ">
                      {getHeaderFilters().includes("organisation") && (
                        <DropdownFilter
                          label={"Organisation"}
                          options={clientList.map((client) => ({
                            value: client?.client_id,
                            label: client?.client_name,
                          }))}
                          selectedOption={{
                            value: user?.client_id,
                            label: user?.client_name,
                          }}
                          onSelect={handleClientSelectChange}
                        />
                      )}
                      {getHeaderFilters().includes("programs") && (
                        <DropdownFilter
                          tour_label="program_filter"
                          label={"Programs"}
                          onSelect={handleProgramSelectChange}
                          options={[
                            { value: null, label: "All" },
                            ...programList.map((program) => ({
                              value: program?.program_id,
                              label: program?.program_name,
                            })),
                          ]}
                          selectedOption={{
                            value: user?.program_id ? user?.program_id : null,
                            label: user?.program_name
                              ? user?.program_name
                              : "All",
                          }}
                        />
                      )}
                      {getHeaderFilters().includes("campaign") && (
                        <DropdownFilter
                          label={"Campaign"}
                          tour_label="campaign_filter"
                          onSelect={handleCampaignSelectChange}
                          options={[
                            { value: null, label: "All" },
                            ...internalCampaignList?.map((campaign) => ({
                              value: campaign?.campaign_id,
                              label: campaign?.campaign_name,
                            })),
                          ]}
                          selectedOption={filter?.filter_campaign}
                        />
                      )}
                      {getHeaderFilters().includes("date_range") && (
                        <DropdownFilter
                          label={"Date"}
                          tour_label="date_filter"
                          onSelect={handleDateRangeSelectChange}
                          options={[
                            { value: null, label: "All Time" },
                            { value: "30", label: "30 Days" },
                            { value: "90", label: "90 Days" },
                            { value: "180", label: "180 Days" },
                            { value: "Custom Range", label: "Custom Range" },
                          ]}
                          selectedOption={
                            filter?.filter_date_range
                              ? filter?.filter_date_range
                              : { value: null, label: "All Time" }
                          }
                        />
                      )}
                      {getHeaderFilters().includes("status") && (
                        <DropdownFilter
                          label={"Status"}
                          tour_label="campaign_status_filter"
                          onSelect={handleStatusSelectChange}
                          options={[
                            { value: null, label: "All" },
                            { value: "completed", label: "Completed" },
                            { value: "active", label: "Active" },
                          ]}
                          selectedOption={filter?.filter_campaign_status}
                        />
                      )}

                      {getHeaderFilters().includes("platform") && (
                        <DropdownFilter
                          label={"Platform"}
                          tour_label="social_media_type_filter"
                          onSelect={handlePlateformSelectChange}
                          options={[
                            { value: "all", label: "All" },
                            { value: "paid_social", label: "Paid Social" },
                            {
                              value: "organic_social",
                              label: "Organic Social",
                            },
                            { value: "paid_media", label: "Paid Media" },
                            { value: "owned_media", label: "Owned Media" },
                            { value: "search", label: "Search" },
                          ]}
                          selectedOption={
                            filter?.filter_platform
                              ? filter?.filter_platform
                              : { value: "all", label: "All" }
                          }
                        />
                      )}

                      {getHeaderFilters().includes("ads_platfrom_filter") && (
                        <DropdownFilter
                          label={"Platform"}
                          onSelect={handleAdsPlateformSelectChange}
                          // options={[
                          //   { value: "all", label: "All" },
                          //   { value: 1, label: "Google Ads" },
                          //   { value: 2, label: "LinkedIn Ads" },
                          //   { value: 3, label: "Snapchat Ads" },
                          //   { value: 4, label: "Spotify Ads" },
                          //   { value: 6, label: "Facebook Ads" },
                          //   { value: "search", label: "Search" },
                          // ]}
                          tour_label="platform_filter"
                          options={[
                            { value: "all", label: "All" },
                            { value: 1, label: "Google " },
                            { value: 2, label: "LinkedIn " },
                            { value: 3, label: "Snapchat " },
                            { value: 4, label: "Spotify " },
                            { value: 6, label: "Facebook " },
                            { value: 7, label: "Instagram" },
                            { value: "search", label: "Search" },
                          ]}
                          selectedOption={filter?.filter_ads_platform}
                        />
                      )}
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
        <div className="d-none d-xl-flex gap-4 align-items-center ">
          <div className="d-flex align-items-center gap_lg">
            <Notifications />
            {/* Sync Progress Percentage */}
            {is_syncing &&
              currentJobCountRef.current > 0 &&
              typeof remaining_job_count === "number" && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    minWidth: 40,
                    marginRight: 8,
                  }}
                >
                  <span
                    style={{ fontWeight: 600, color: "#7F56D9", fontSize: 16 }}
                  >
                    {Math.max(
                      1,
                      Math.ceil(
                        (100 *
                          (currentJobCountRef.current - remaining_job_count)) /
                          currentJobCountRef.current,
                      ),
                    )}
                    %
                  </span>

                  <div
                    style={{
                      width: 48,
                      height: 6,
                      background: "#eee",
                      borderRadius: 4,
                      marginTop: 2,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.max(
                          1,
                          Math.ceil(
                            (100 *
                              (currentJobCountRef.current -
                                remaining_job_count)) /
                              currentJobCountRef.current,
                          ),
                        )}%`,
                        height: "100%",
                        background: "#7F56D9",
                        borderRadius: 4,
                        transition: "width 0.3s",
                      }}
                    />
                  </div>
                </div>
              )}
            <div
              style={{
                cursor: is_syncing ? "not-allowed" : "pointer",
                opacity: is_syncing ? 0.6 : 1,
                transition: "opacity 0.2s",
              }}
              onClick={is_syncing ? undefined : handleSyncData}
              className={`sync_data ${is_syncing ? "syncing-rotating" : ""}`}
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title={
                is_syncing
                  ? "Syncing in progress"
                  : "Fetch and update the most recent post and ad data from all connected platforms."
              }
            >
              <img
                className=""
                src={getIconSrc("sync", "svg")}
                style={{
                  animation: is_syncing
                    ? "sync-rotate 1s linear infinite"
                    : "none",
                  pointerEvents: is_syncing ? "none" : "auto",
                  width: 24,
                  height: 24,
                }}
                alt="Sync"
              />
            </div>
            <div
              style={{ cursor: "pointer" }}
              onClick={resetClientTour}
              className="btn-reset-tour"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Replay Client Tour"
            >
              <span>
                {getSvgIcons("tour", {
                  fontSize: "24px",
                  color: isReportsPage ? "#D6BBFB" : "#7f56d9",
                })}
              </span>
            </div>

            {/* {!user?.is_super_admin && (
              <div
                style={{ cursor: "pointer" }}
                className="flex justify-center bg-[#7F56D9] px-3 py-2 rounded-full "
              >
                <img className="border border-white rounded-full" src={getIconSrc("export", "svg")}></img>
                <span className="text-white text-[15px] ml-2">
                  Export Stats
                </span>
              </div>
            )} */}
          </div>
        </div>
      </div>
      <DateRangePicker
        showDateRangePicker={showDateRangePicker}
        setShowDateRangePicker={setShowDateRangePicker}
        dateRange={customRange}
        setDateRange={setCustomRange}
      />
    </div>
  );
};

const SuperAdminNavbar = ({
  setIsOpenSidebar,
  clientList,
  handleClientSelectChange,
  customStyles,
  user,
  location,
}) => {
  return (
    <div className="navbar d-flex align-items-center px-4 py-3 border_bottom border-2">
      <div className="d-flex justify-content-between w-100">
        <i
          className="bi bi-justify-left d-block d-xl-none fs-2 p-2"
          style={{ width: "10px" }}
          onClick={() => setIsOpenSidebar(true)}
        ></i>
        <NavMobileMenu
          handleClientSelectChange={handleClientSelectChange}
          customStyles={customStyles}
          clientList={clientList}
        />
      </div>
      <div className="row d-flex align-items-center w-100 h-100 g-0 d-none d-xl-flex ">
        <div className="col-md-8">
          <span className="fw-bold fs-4">
            {getCurrentPageName(location.pathname)}
          </span>
        </div>
        <div className="col-md-4 gap_lg d-flex justify-content-end align-items-center">
          <div className="d-flex gap_lg">{/* <Notifications /> */}</div>
          <div className="d-flex flex-column ">
            <span className="d-flex justify-content-end">Hello</span>
            <span className="fw-bold d-flex justify-content-end text-truncate">
              {user?.first_name}
            </span>
          </div>
          <div>
            {user?.profile_image ? (
              <img
                width={50}
                height={50}
                src={user?.profile_image}
                className="img-fluid rounded rounded-5 "
                alt=""
              />
            ) : (
              <div
                style={{ width: "4vw", height: "4vw", borderRadius: "50%" }}
                className=" d-flex justify-content-center align-items-center"
              ></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Navbar = ({ setIsOpenSidebar, navbarRef }) => {
  const user = useSelector((state) => state?.user);
  const [clientList, setClientList] = useState([]);
  const [programList, setProgramList] = useState([]);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const getClientList = async () => {
    try {
      let method = "POST";
      let headers = {};
      let formData = new FormData();
      formData.append("user_id", user?.user_id);
      let data = formData;

      let result = await handleApi(
        `user_client_mapping`,
        method,
        headers,
        data,
      );
      if (result?.success) {
        setClientList(
          result?.data?.user_client_mapping_info.sort((a, b) =>
            a.client_name.localeCompare(b.client_name),
          ),
        );
        dispatch(
          UPDATE_USER_DATA({
            clients: result?.data?.user_client_mapping_info.sort((a, b) =>
              a.client_name.localeCompare(b.client_name),
            ),
          }),
        );
      }
    } catch (e) {
      toast.error(e?.message ? e?.message : "Failed to fetch client list");
    } finally {
    }
  };

  const getProgramList = async () => {
    try {
      let method = "POST";
      let headers = {};
      let formData = new FormData();
      formData.append("client_id", user?.client_id);
      formData.append("role_id", user?.role_id);
      formData.append("user_id", user?.user_id);
      let data = formData;
      let result = await handleApi(`program_list`, method, headers, data);
      if (result?.success) {
        setProgramList(result?.data?.programs_list);
      } else {
        console.log(result?.error?.error_message);
      }
    } catch (e) {
      console.log(e);
      toast.error(e?.message ? e?.message : "Failed to fetch client list");
    } finally {
    }
  };

  const handleClientSelectChange = (selected_option) => {
    let client_data = clientList.find(
      (client) => client?.client_id == selected_option?.value,
    );

    if (!clientHasAccess(client_data)) {
      toast.error(
        user?.role_id < 3
          ? "Your trial or subscription has expired. Please subscribe to continue."
          : "Your organization's subscription has expired. Please contact your administrator.",
      );
      navigate(getSubscriptionLockedFallbackPath(user));
    } else if (client_data?.is_blocked) {
      toast.error(
        "This U-Intelligence account has been blocked. Please contact the Admin for assistance.",
      );
    } else {
      if (client_data?.subscription_access) {
        applySubscriptionAccess(client_data.subscription_access);
      }
      dispatch(
        UPDATE_USER_DATA({
          client_id: selected_option?.value,
          client_name: selected_option?.label,
          role_id: client_data?.role_id,
          is_agency: client_data?.is_agency,
          total_members: client_data?.total_members,
          program_id: null,
          program_name: "All",
        }),
      );
    }
  };

  const handleProgramSelectChange = (selected_option) => {
    dispatch(
      UPDATE_USER_DATA({
        program_id: selected_option?.value,
        program_name: selected_option?.label,
      }),
    );
  };

  useEffect(() => {
    getClientList();
  }, [user?.client_name]);

  useEffect(() => {
    if (user?.client_id) {
      getProgramList();
    }
  }, [user?.client_id, user?.count_of_programs, user?.profile_image]);

  return (
    <div ref={navbarRef}>
      {!user?.is_super_admin ? (
        <UserNavbar
          setIsOpenSidebar={setIsOpenSidebar}
          clientList={clientList}
          programList={programList}
          handleClientSelectChange={handleClientSelectChange}
          handleProgramSelectChange={handleProgramSelectChange}
          customStyles={customStyles}
          user={user}
          location={location}
        />
      ) : (
        <SuperAdminNavbar
          setIsOpenSidebar={setIsOpenSidebar}
          clientList={clientList}
          handleClientSelectChange={handleClientSelectChange}
          customStyles={customStyles}
          user={user}
          location={location}
        />
      )}
    </div>
  );
};

export default Navbar;
